import React, { useState, useEffect } from "react";
import { ShoppingCart, Bot, Save, Play, Ban, RotateCcw, ListChecks, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import PlatformCard from "./PlatformCard";
import AutoKeepLogs from "./AutoKeepLogs";

export default function AutoKeepView({ write }) {
  const [cfg, setCfg] = useState(null);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningCashier, setRunningCashier] = useState(false);
  const [runningCancel, setRunningCancel] = useState(false);
  const [runningReprocess, setRunningReprocess] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);
  const [autoStatus, setAutoStatus] = useState(null);

  useEffect(() => {
    api.get("/desty/autokeep-config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const load = () => api.get("/desty/automation-status").then((r) => setAutoStatus(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/desty/autokeep-config", cfg); toast.success("Pengaturan Auto Keep disimpan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const s = (await api.post("/desty/autokeep-run")).data || {};
      if (s.ran === false) toast.error(s.reason || "Auto Keep nonaktif");
      else toast.success(`Proses selesai: ${s.created || 0} dibuat, ${s.failed || 0} gagal (dari ${s.processed || 0})`);
      setReloadSignal((x) => x + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };
  const runCashier = async () => {
    setRunningCashier(true);
    try {
      const s = (await api.post("/desty/autocashier-run")).data || {};
      if (s.ran === false) toast.error(s.reason || "Auto Kasir nonaktif");
      else toast.success(`Auto Kasir: ${s.converted || 0} jadi sales, ${s.failed || 0} gagal (dari ${s.processed || 0})`);
      setReloadSignal((x) => x + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunningCashier(false); }
  };
  const runCancel = async () => {
    setRunningCancel(true);
    try {
      const { data } = await api.post("/desty/autocancel-run");
      let s = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await api.get(`/desty/autocancel-run/${data.job_id}`);
        if (st.data.status === "done") { s = st.data.result; break; }
        if (st.data.status === "error") throw new Error(st.data.error);
      }
      if (!s) throw new Error("Pemindaian Desty terlalu lama, coba lagi.");
      if (s.ran === false) toast.error(s.reason || "Auto Cancel nonaktif");
      else toast.success(`Auto Cancel: ${s.cancelled || 0} keep dibatalkan, ${s.in_sale || 0} sudah jadi sales, ${s.failed || 0} gagal (dari ${s.processed || 0})`);
      setReloadSignal((x) => x + 1);
    } catch (e) { toast.error(e.response ? formatApiError(e.response?.data?.detail) : e.message); }
    finally { setRunningCancel(false); }
  };
  const runReprocess = async () => {
    setRunningReprocess(true);
    try {
      const s = (await api.post("/desty/autoreprocess-run")).data || {};
      if (s.ran === false) toast.error(s.reason || "Auto Reprocess nonaktif");
      else toast.success(`Auto Reprocess: ${s.recovered || 0} keep dipulihkan, ${s.failed || 0} gagal (dicek ${s.checked || 0})`);
      setReloadSignal((x) => x + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunningReprocess(false); }
  };
  const reconcile = async () => {
    setReconciling(true);
    try {
      const poll = async () => {
        for (let i = 0; i < 300; i++) {
          await new Promise((r) => setTimeout(r, 5000));
          const st = (await api.get("/desty/reconcile-keeps/status")).data || {};
          if (!st.running && (st.phase === "done" || st.phase === "error")) return st;
        }
        return null;
      };
      await api.post("/desty/reconcile-keeps/start", { apply: false });
      toast.info("Mengecek order Desty di latar belakang… bisa beberapa menit, mohon tunggu.");
      let st = await poll();
      if (!st) { toast.info("Proses masih berjalan lama. Coba klik lagi nanti untuk lihat hasilnya."); return; }
      if (st.phase === "error") { toast.error(st.error || "Gagal cek Desty"); return; }
      const d = st.result || {};
      if (!d.missing) {
        toast.info(`Semua ${d.total} order belum terkirim sudah punya keep (${d.already} keep aktif, ${d.in_sale} sudah jadi Sales). Tidak ada yang kurang.`);
        return;
      }
      if (!window.confirm(`Dari ${d.total} order belum terkirim di Desty:\n• ${d.already} sudah ada keep aktif\n• ${d.in_sale} sudah jadi Sales\n• ${d.missing} BELUM punya keep\n\nBuatkan keep untuk ${d.missing} order yang belum punya sekarang? (idempotent, tidak dobel)`)) return;
      await api.post("/desty/reconcile-keeps/start", { apply: true });
      toast.info("Membuat keep di latar belakang… mohon tunggu.");
      st = await poll();
      if (!st) { toast.info("Proses pembuatan keep masih berjalan. Cek Log Auto Reprocess beberapa saat lagi."); setReloadSignal((x) => x + 1); return; }
      if (st.phase === "error") { toast.error(st.error || "Gagal sinkron"); return; }
      const r = st.result || {};
      toast.success(`Sinkron selesai: ${r.created} keep dibuat, ${r.failed} gagal (dari ${r.missing} yang kurang).`);
      setReloadSignal((x) => x + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReconciling(false); }
  };

  if (!cfg) return <Panel className="p-8 text-center text-cocoa/50">Memuat…</Panel>;

  return (
    <div className="space-y-6">
      <Panel className="p-4 space-y-4">
        {autoStatus && (
          <div data-testid="desty-automation-status" className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium border ${autoStatus.stale ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
            {autoStatus.stale ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <CheckCircle2 className="w-4 h-4 shrink-0" />}
            <span>
              {autoStatus.stale
                ? `Otomatisasi mungkin berhenti — terakhir jalan ${autoStatus.minutes_since == null ? "belum pernah tercatat" : autoStatus.minutes_since + " menit lalu"}. Keep/kasir order baru bisa tertunda.`
                : `Otomatisasi aktif — terakhir jalan ${autoStatus.minutes_since ?? 0} menit lalu (jadwal tiap ${autoStatus.interval_minutes} menit).`}
            </span>
            {autoStatus.summary && !autoStatus.stale && (
              <span className="ml-auto text-emerald-600/80 hidden sm:inline">Run terakhir: {autoStatus.summary.autokeep_created || 0} keep · {autoStatus.summary.autocashier_converted || 0} kasir · {autoStatus.summary.autocashier_failed || 0} gagal</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-semibold text-cocoa flex items-center gap-2"><Bot className="w-4 h-4" /> Pengaturan Auto Keep</div>
            <p className="text-xs text-cocoa/60">Sistem otomatis membuat keep dari order baru Desty tiap 15 menit. Harga item mengikuti selling price ERP.</p>
          </div>
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <div className="flex items-center gap-2"><span className="text-cocoa/60">Auto Keep</span>
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="ak-master-enabled" /></div>
            <div className="flex items-center gap-2"><span className="text-cocoa/60">Auto Kasir</span>
              <Switch checked={!!cfg.cashier_enabled} onCheckedChange={(v) => setCfg({ ...cfg, cashier_enabled: v })} data-testid="ak-master-cashier" /></div>
            <div className="flex items-center gap-2"><span className="text-cocoa/60">Auto Cancel</span>
              <Switch checked={!!cfg.autocancel_enabled} onCheckedChange={(v) => setCfg({ ...cfg, autocancel_enabled: v })} data-testid="ak-master-autocancel" /></div>
            <div className="flex items-center gap-2"><span className="text-cocoa/60">Auto Reprocess</span>
              <Switch checked={!!cfg.auto_reprocess_enabled} onCheckedChange={(v) => setCfg({ ...cfg, auto_reprocess_enabled: v })} data-testid="ak-master-reprocess" /></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PlatformCard platform="shopee" data={cfg.shopee} groups={groups} onChange={(d) => setCfg({ ...cfg, shopee: d })} />
          <PlatformCard platform="tiktok" data={cfg.tiktok} groups={groups} onChange={(d) => setCfg({ ...cfg, tiktok: d })} />
        </div>
        <div className="rounded-lg border border-cocoa/10 bg-ivory/40 p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <div className="flex-1">
            <div className="text-sm font-medium text-cocoa">Prioritas Batch PO (Pre-Order)</div>
            <p className="text-xs text-cocoa/60">Jika 1 order berisi item dari beberapa batch, batch mana yang diisi lebih dulu saat Auto Keep. Setiap item tetap dialokasikan ke batch yang sisanya cukup.</p>
          </div>
          <Select value={cfg.po_batch_priority || "earliest_arrival"} onValueChange={(v) => setCfg({ ...cfg, po_batch_priority: v })} disabled={!write}>
            <SelectTrigger className="w-full sm:w-64 bg-white" data-testid="ak-po-batch-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="earliest_arrival">Estimasi datang paling awal</SelectItem>
              <SelectItem value="latest_arrival">Estimasi datang paling akhir</SelectItem>
              <SelectItem value="earliest_close">Batch tutup paling awal</SelectItem>
              <SelectItem value="most_remaining">Sisa kuota terbanyak</SelectItem>
              <SelectItem value="least_remaining">Sisa kuota paling sedikit (habiskan dulu)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {write && (
          <div className="flex gap-2 justify-end flex-wrap">
            <Button variant="outline" onClick={runNow} disabled={running} data-testid="ak-run-now">
              <Play className={`w-4 h-4 mr-1.5 ${running ? "animate-pulse" : ""}`} /> {running ? "Memproses…" : "Jalankan Auto Keep"}
            </Button>
            <Button variant="outline" onClick={runCashier} disabled={runningCashier} data-testid="ak-run-cashier">
              <ShoppingCart className={`w-4 h-4 mr-1.5 ${runningCashier ? "animate-pulse" : ""}`} /> {runningCashier ? "Memproses…" : "Jalankan Auto Kasir"}
            </Button>
            <Button variant="outline" onClick={runCancel} disabled={runningCancel} data-testid="ak-run-cancel">
              <Ban className={`w-4 h-4 mr-1.5 ${runningCancel ? "animate-pulse" : ""}`} /> {runningCancel ? "Memproses…" : "Jalankan Auto Cancel"}
            </Button>
            <Button variant="outline" onClick={runReprocess} disabled={runningReprocess} data-testid="ak-run-reprocess">
              <RotateCcw className={`w-4 h-4 mr-1.5 ${runningReprocess ? "animate-spin" : ""}`} /> {runningReprocess ? "Memproses…" : "Jalankan Auto Reprocess"}
            </Button>
            <Button variant="outline" onClick={reconcile} disabled={reconciling} className="border-amber-500 text-amber-700 hover:bg-amber-50" data-testid="ak-reconcile-keeps">
              <ListChecks className={`w-4 h-4 mr-1.5 ${reconciling ? "animate-pulse" : ""}`} /> {reconciling ? "Mengecek…" : "Cek & Sinkron Keep Desty"}
            </Button>
            <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="ak-save">
              <Save className="w-4 h-4 mr-1.5" /> {saving ? "Menyimpan…" : "Simpan Pengaturan"}
            </Button>
          </div>
        )}
      </Panel>

      <AutoKeepLogs write={write} reloadSignal={reloadSignal} />
    </div>
  );
}
